// anhang_register — the REF | ANFORDERUNG | PRIO | ERFÜLLT DURCH annex table (v15 slides
// 15–17): requirements grouped by category, one category per page run, MUST bold, descoped
// requirements filtered out and named in the footline. Row placement (y/height) comes from
// the bound resolver's pagination; `page` picks which rendered page to draw.
import type { AnhangBound, AnhangRegisterContent } from '../../../../shared/deckTypes.js';
import { Abs, Brand, Dkp, Eyebrow, PageNo, Rule } from './chrome.js';

/** The frozen priority vocabulary of the annex table. */
const PRIO_LABEL: Record<string, string> = { must: 'MUSS', should: 'SOLL', could: 'KANN', wont: '—' };
const PRIO_STYLE: Record<string, { color: string; fontWeight: number }> = {
  must: { color: '#121316', fontWeight: 700 },
  should: { color: '#3A3D42', fontWeight: 500 },
  could: { color: '#6B6E75', fontWeight: 500 },
  wont: { color: '#6B6E75', fontWeight: 500 },
};

export default function AnhangRegisterPreview({ content, bound, page = 0, pageNo }: {
  content: AnhangRegisterContent;
  bound?: AnhangBound;
  page?: number;
  pageNo?: number | null;
}) {
  const c = content;
  const pages = bound?.pages ?? [];
  const p = pages[page];
  const eyebrow = c.eyebrow
    ? pages.length > 1 ? `${c.eyebrow} · ${page + 1}/${pages.length}` : c.eyebrow
    : '';
  const descoped = c.descoped_note
    || (bound?.descopedRefs.length ? `Nicht im Umfang: ${bound.descopedRefs.join(' · ')}` : '');

  return (
    <Dkp>
      <Eyebrow text={eyebrow} />
      <Abs x={64} y={84} w={1000}>
        <div className="dkp-title" style={{ fontSize: 25.3 }}>{p?.title ?? ''}</div>
      </Abs>

      {/* table head (frozen) */}
      <Abs x={64} y={140}><span className="dkp-th">REF</span></Abs>
      <Abs x={140} y={140}><span className="dkp-th">ANFORDERUNG</span></Abs>
      <Abs x={984} y={140}><span className="dkp-th">PRIO</span></Abs>
      <Abs x={1056} y={140}><span className="dkp-th">ERFÜLLT DURCH</span></Abs>
      <Rule x={64} y={160} w={1152} color="#121316" />

      {(p?.rows ?? []).map((r) => {
        const prio = PRIO_STYLE[r.priority] ?? PRIO_STYLE.should;
        return (
          <div key={r.ref}>
            <Abs x={64} y={r.y + 2} w={70}>
              <span className="dkp-mono" style={{ fontSize: 9.3, fontWeight: 700, color: '#8A6F3E' }}>{r.ref}</span>
            </Abs>
            <Abs x={140} y={r.y} w={830}>
              <div className="dkp-clamp2" style={{
                fontSize: 12, fontWeight: 500, color: '#3A3D42', lineHeight: '19.5px',
              }}>{r.statement}</div>
            </Abs>
            <Abs x={984} y={r.y + 2} w={60}>
              <span className="dkp-mono" style={{ fontSize: 9.3, letterSpacing: '.08em', ...prio }}>
                {PRIO_LABEL[r.priority] ?? r.priority}
              </span>
            </Abs>
            <Abs x={1056} y={r.y + 2} w={160}>
              <span className="dkp-mono" style={{ fontSize: 9.3, color: '#6B6E75' }}>
                {r.delRefs.join(' · ')}
              </span>
            </Abs>
            <Rule x={64} y={r.y + r.height + 7} w={1152} />
          </div>
        );
      })}

      {descoped ? (
        <Abs x={64} y={658} w={1152}>
          <span className="dkp-footline">{descoped}</span>
        </Abs>
      ) : null}

      <Brand />
      <PageNo n={pageNo} />
    </Dkp>
  );
}
